import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { useCreateGroupForDashboard } from 'features/libra/ticket/libraTicketsApi'
import { useGeckoNotifications } from 'features/notifications'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'

import { CreateGroupModal } from './CreateGroupModal'

type LibraDashboardGroupSelectProps = {
  groups: ReadonlyArray<{ id: string; title: string }>
  selectedGroupId?: string
  setSelectedGroupId: Dispatch<SetStateAction<string | undefined>>
  organizationId?: string
  onGroupCreated?: () => void
}

export const LibraDashboardGroupSelect: FC<LibraDashboardGroupSelectProps> = ({
  groups,
  selectedGroupId,
  setSelectedGroupId,
  organizationId,
  onGroupCreated,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const createGroup = useCreateGroupForDashboard()
  const { successNotification, errorNotification } = useGeckoNotifications()

  const handleCreateGroup = useCallback(
    async (title: string) => {
      if (!organizationId) {
        errorNotification('Organization ID is required to create a group')
        return
      }

      setIsCreating(true)
      try {
        await createGroup({
          title,
          organization: organizationId,
        })
        successNotification('Group created successfully')
        setIsModalOpen(false)
        onGroupCreated?.()
      } catch (error) {
        console.error('Failed to create group:', error)
        errorNotification('Failed to create group')
      } finally {
        setIsCreating(false)
      }
    },
    [
      organizationId,
      createGroup,
      successNotification,
      errorNotification,
      onGroupCreated,
    ]
  )

  return (
    <>
      <Container>
        <ListContainer>
          {groups.map((group) => (
            <GroupButton
              key={group.id}
              type='button'
              $isSelected={group.id === selectedGroupId}
              onClick={() => setSelectedGroupId(group.id)}>
              {group.title}
            </GroupButton>
          ))}
        </ListContainer>
        <AddButton
          type='button'
          onClick={() => setIsModalOpen(true)}
          aria-label='Create new group'>
          <PlusOutlined />
        </AddButton>
      </Container>
      <CreateGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreateGroup={handleCreateGroup}
        isCreating={isCreating}
      />
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const GroupButton = styled.button<{ $isSelected: boolean }>`
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};
  color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
  }
`

const AddButton = styled.button`
  border: 1px dashed ${({ theme }) => theme.colors.gray[700]};
  background: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
    color: ${({ theme }) => theme.forecast.primary};
  }
`
